import { useEffect, type JSX } from 'react';
import { useIntl } from 'react-intl';
import NullStateAction from '@alltrails/amplitude/enums/NullStateAction';
import NullStateAnalytic from '@alltrails/amplitude/enums/NullState';
import NullState from '@alltrails/denali/components/NullState';
import Tent from '@alltrails/denali/illustrations/Tent';
import type { TrailId } from '@alltrails/types/TrailId';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { useLazySearchReviewsQuery } from '../../../slices/reviewsApi';
import { useAppSelector } from '../../../redux';
import styles from './SearchError.module.scss';

type SearchErrorProps = {
  onCtaClick?: (action: NullStateAction, state: NullStateAnalytic) => void;
  onViewed?: (state: NullStateAnalytic) => void;
  trailId: TrailId;
};

const SearchError = ({ onCtaClick, onViewed, trailId }: SearchErrorProps): JSX.Element => {
  const intl = useIntl();
  const isMobile = useIsMobileSizedScreen();
  const { query, entityType, ratings, activities, months } = useAppSelector(state => state.reviewsSearchFilters.permanent);
  const [trigger] = useLazySearchReviewsQuery();

  const retry = () => {
    onCtaClick?.('retry', window.navigator.onLine ? 'server error' : 'no service');
    trigger({
      query,
      photosOnly: entityType === 'photosOnly',
      rating: ratings,
      activity: activities,
      month: months,
      trailId
    });
  };

  useEffect(() => {
    onViewed?.(window.navigator.onLine ? 'server error' : 'no service');
  }, [onViewed]);

  return (
    <div className={styles.container}>
      {window.navigator.onLine ? (
        <NullState
          buttons={{
            [isMobile ? 'primary' : 'default']: {
              onClick: retry,
              testId: 'reviews-search-error-cta',
              text: intl.formatMessage({ defaultMessage: 'Try again' })
            }
          }}
          className={styles.error}
          description={intl.formatMessage({
            defaultMessage: 'It’s us, not you! Try clearing your search or check back later.'
          })}
          illustration={{ Component: Tent }}
          noTopMargin
          title={intl.formatMessage({ defaultMessage: 'Something’s not working' })}
        />
      ) : (
        <NullState
          buttons={{
            default: {
              onClick: retry,
              testId: 'reviews-search-offline-cta',
              text: intl.formatMessage({ defaultMessage: 'Try again' })
            }
          }}
          className={styles.error}
          description={intl.formatMessage({ defaultMessage: 'Check your internet connection.' })}
          illustration={{ Component: Tent }}
          noTopMargin
          title={intl.formatMessage({ defaultMessage: 'You’re offline' })}
        />
      )}
    </div>
  );
};

export default SearchError;
